// src/tours/TourController.jsx — mounted once in DashboardLayout (v1.4 TOUR-1).
// On a role's home page, the first visit of a release starts that role's
// tour; after it (or when it was already seen) the release's "What's new"
// shows once. Both are recorded per user per version on the backend
// (GET /accounts/me/tours/, POST <key>/seen/). The header's "Take the tour"
// re-runs the tour through the `lpms:tour` event without clearing anything.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getRoleHomePath } from '../config/navigation';
import { startTour, shouldStartTour } from './runTour';
import { whatsNewFor, whatsNewKey } from './whatsNew';

export const TOUR_EVENT = 'lpms:tour';

function WhatsNewDialog({ items, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" role="dialog" aria-modal="true" aria-labelledby="whatsnew-title">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 id="whatsnew-title" className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={17} className="text-blue-600" /> What's new
          </h2>
          <button onClick={onClose} aria-label="Close" className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={17} /></button>
        </div>
        <ul className="p-5 space-y-3 text-sm text-gray-700 list-disc pl-9">
          {items.map((i) => <li key={i.text}>{i.text}</li>)}
        </ul>
        <div className="px-5 pb-5">
          <button onClick={onClose} autoFocus className="btn-primary w-full">Got it</button>
        </div>
      </div>
    </div>
  );
}

export default function TourController() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const role = user?.role;
  const brandName = user?.brand?.name || '';
  const modulesAvailable = user?.modules_available === true;
  const [seen, setSeen] = useState(null);
  const [showNews, setShowNews] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!role) return;
    api.get('/accounts/me/tours/')
      .then((res) => setSeen(res.data?.data || []))
      .catch(() => setSeen(null));
  }, [role]);

  const offerNews = useCallback((rows) => {
    if (whatsNewFor(role, { modulesAvailable }).length && !(rows || []).some((r) => r.key === whatsNewKey())) setShowNews(true);
  }, [role, modulesAvailable]);

  // First visit of a release, on the role's home page (where its anchors are).
  useEffect(() => {
    if (!seen || started.current || pathname !== getRoleHomePath(role)) return undefined;
    started.current = true;
    if (!shouldStartTour(role, seen)) { offerNews(seen); return undefined; }
    const t = setTimeout(() => {   // let the page's cards render first
      const tour = startTour(role, { brandName, modulesAvailable, onDone: () => offerNews(seen) });
      if (!tour) offerNews(seen);
    }, 800);
    return () => clearTimeout(t);
  }, [seen, pathname, role, brandName, modulesAvailable, offerNews]);

  useEffect(() => {
    const run = () => startTour(role, { brandName, modulesAvailable });
    window.addEventListener(TOUR_EVENT, run);
    return () => window.removeEventListener(TOUR_EVENT, run);
  }, [role, brandName, modulesAvailable]);

  async function closeNews() {
    setShowNews(false);
    try { await api.post(`/accounts/me/tours/${whatsNewKey()}/seen/`); } catch { /* shown again next time */ }
  }

  return showNews ? <WhatsNewDialog items={whatsNewFor(role, { modulesAvailable })} onClose={closeNews} /> : null;
}
